/**
 * Este componente renderiza a tela de vinculação de conta
 * Permite inserir um código de convite ou gerenciar um vínculo existente
 * 
 * Cenários de uso:
 * 1. Quando o usuário deseja vincular sua conta usando um código
 * 2. Quando o usuário deseja gerenciar um vínculo existente
 */
import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import H1 from "../../../../../components/UI/Titles/H1/h1";
import RoundedButton from "../../../../../components/Buttons/RoundedButton";
import StateView from '../shared/StateView';
import LinkMessage from '../shared/LinkMessage';
import classes from '../../RescueCode.module.scss';
import SystemContext from "../../../../../context/SystemContext";
import * as kApi from "../../../../../constants/api";
import { Auth } from "@aws-amplify/auth";
import Spinner from "../../../../../components/UI/Spinner";
import { STATES } from "../../utils/constants";

/**
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.currentParentUser - Objeto contendo informações do profissional atualmente vinculado
 * @param {Function} props.redirectToDashboard - Função para redirecionar o usuário ao painel principal
 * @param {Function} props.showUnlinkScreen - Função para mostrar a tela de desvinculação
 * @param {Function} props.onCodeSubmit - Função para processar o código de convite
 * @returns {JSX.Element} - Elemento React renderizado
 */
const NoInviteView = ({ 
  currentParentUser, 
  redirectToDashboard, 
  showUnlinkScreen,
  onCodeSubmit 
}) => {
  const navigate = useNavigate();
  const { setLoginInfo } = useContext(SystemContext);
  
  // Estados para gerenciar a interface e o processo
  const [inviteCode, setInviteCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processingStep, setProcessingStep] = useState("input"); // input, validating, confirming, result
  const [professionalInfo, setProfessionalInfo] = useState(null);
  
  // Verifica se o usuário tem um vínculo atual com algum profissional
  const hasCurrentLink = currentParentUser && Object.keys(currentParentUser).length > 0;

  // Atualiza o código quando o usuário digita
  const handleCodeChange = (e) => {
    setInviteCode(e.target.value.toUpperCase());
    if (codeError) {
      setCodeError("");
    }
  };

  // Processa o envio do código
  const handleSubmitCode = async (e) => {
    e.preventDefault();
    
    // Validação básica
    if (!inviteCode || !inviteCode.trim()) {
      setCodeError("Por favor, insira um código de convite válido.");
      return;
    }
    
    // Iniciar processamento
    setIsSubmitting(true);
    setProcessingStep("validating");
    
    try {
      // Etapa 1: Verificar se o código é válido
      const checkResponse = await fetch(`${kApi.API_Q_CODE}/${inviteCode.trim()}`);
      
      if (!checkResponse.ok) {
        // Tratamento de erros específicos em português
        if (checkResponse.status === 404) {
          setCodeError("Código não encontrado. Verifique e tente novamente.");
        } else if (checkResponse.status === 406) {
          setCodeError("Este código não pode ser usado pelo mesmo usuário que o criou.");
        } else if (checkResponse.status === 405) {
          setCodeError("Este código já foi utilizado por outro usuário.");
        } else if (checkResponse.status === 409) {
          setCodeError("Seu perfil já está vinculado a um profissional.");
        } else {
          try {
            // Tentar obter a mensagem de erro do corpo da resposta
            const errorData = await checkResponse.json();
            if (errorData && errorData.message) {
              // Tratando especificamente o erro "the code provided is already used"
              if (errorData.message.includes("code provided is already used")) {
                setCodeError("O código fornecido já foi utilizado por outro usuário.");
              } else {
                setCodeError(errorData.message);
              }
            } else {
              setCodeError("Erro ao validar o código. Tente novamente mais tarde.");
            }
          } catch (e) {
            setCodeError("Erro ao validar o código. Tente novamente mais tarde.");
          }
        }
        setIsSubmitting(false);
        setProcessingStep("input");
        return;
      }
      
      // Código é válido, obter informações do profissional
      const professionalData = await checkResponse.json();
      setProfessionalInfo(professionalData);
      
      // Etapa 2: Confirmar a vinculação
      setProcessingStep("confirming");
      
      // Obter o token de autenticação
      const token = Auth?.user?.signInUserSession?.idToken?.jwtToken;
      if (!token) {
        setCodeError("Você precisa estar autenticado para vincular seu perfil.");
        setIsSubmitting(false);
        setProcessingStep("input");
        return;
      }
      
      // Fazer a chamada para confirmar a vinculação
      const confirmResponse = await fetch(`${kApi.API_Q_CODE}/${inviteCode.trim()}`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      });
      
      if (!confirmResponse.ok) {
        // Tratamento de erros específicos da confirmação em português
        try {
          const errorData = await confirmResponse.json();
          if (errorData && errorData.message) {
            // Tradução de mensagens de erro específicas
            if (errorData.message.includes("code provided is already used")) {
              setCodeError("O código fornecido já foi utilizado por outro usuário.");
            } else if (errorData.message.includes("already linked")) {
              setCodeError("Seu perfil já está vinculado a este profissional.");
            } else if (errorData.message.includes("not found")) {
              setCodeError("Código de convite não encontrado ou expirado.");
            } else {
              setCodeError(errorData.message || "Não foi possível aceitar o convite. Tente novamente.");
            }
          } else {
            setCodeError("Não foi possível aceitar o convite. Tente novamente.");
          }
        } catch (e) {
          setCodeError("Não foi possível aceitar o convite. Tente novamente.");
        }
        setIsSubmitting(false);
        setProcessingStep("input");
        return;
      }
      
      // Vinculação concluída com sucesso
      setProcessingStep("result");
      
      // Atualizar o contexto global e redirecionar
      setLoginInfo(prevState => ({
        ...prevState,
        code: null
      }));
      
      // Atualiza a sessão se necessário
      if (global.updateSession) {
        global.updateSession();
      }
      
      // Redirecionar para a tela de sucesso ou para o painel
      setTimeout(() => {
        window.location.href = "/vincular/sucesso";
      }, 1000);
      
    } catch (error) {
      console.error("Erro ao processar o código:", error);
      setCodeError("Ocorreu um erro ao processar o código. Por favor, tente novamente.");
      setIsSubmitting(false);
      setProcessingStep("input");
    }
  };

  // Tela de processamento durante a validação e confirmação
  if (processingStep === "validating" || processingStep === "confirming") {
    return (
      <StateView className={classes.spinnerContainer}>
        <div style={{ textAlign: 'center' }}>
          <H1 className={classes.noInviteTitle}>
            {processingStep === "validating" ? "Validando código..." : "Vinculando perfil..."}
          </H1>
          <Spinner />
          <p style={{ marginTop: '20px', color: '#666' }}>
            {processingStep === "validating" 
              ? "Verificando o código inserido." 
              : "Vinculando seu perfil ao profissional."}
          </p>
        </div>
      </StateView>
    );
  }

  // Tela de resultado após processamento bem-sucedido
  if (processingStep === "result") {
    return (
      <StateView className={classes.successContainer}>
        <H1 className={classes.successTitle}>Tudo certo!</H1>
        <LinkMessage className={classes.successMessage}>
          <p>Seu perfil foi vinculado com sucesso ao profissional.</p>
          <p>Você será redirecionado em instantes...</p>
        </LinkMessage>
        <div className={classes.successIcon} style={{ fontSize: '60px', color: '#4CAF50' }}>✓</div>
      </StateView>
    );
  }

  // Tela principal para inserção do código ou gerenciamento de vínculo
  return (
    <StateView className={classes.noInviteContainer}>
      {hasCurrentLink ? (
        // BLOCO 1: Usuário com vínculo existente - Interface de gerenciamento
        <>
          <H1 className={classes.noInviteTitle}>Gerenciamento de Vínculo</H1>

          <div className={classes.currentLinkInfo}>
            <p>Seu perfil está vinculado à <strong>{currentParentUser.given_name || "um profissional"}</strong>.</p>
            
            <RoundedButton
              className={classes.manageButton}
              text="GERENCIAR VÍNCULO"
              onClick={showUnlinkScreen}
            />
          </div>
        </>
      ) : (
        // BLOCO 2: Interface para o usuário inserir um código de vinculação
        <>
          <H1 className={classes.noInviteTitle}>Possui um código?</H1>
          
          <LinkMessage className={classes.noInviteMessage}>
            <p>INSIRA O CÓDIGO:</p>
          </LinkMessage>
          
          <form onSubmit={handleSubmitCode} className={classes.codeInputForm} style={{ width: '100%', maxWidth: '500px' }}>
            <input
              type="text"
              value={inviteCode}
              onChange={handleCodeChange}
              placeholder="Código"
              className={classes.codeInput}
              style={{
                width: '100%',
                padding: '15px',
                borderRadius: '30px',
                border: '1px solid #ccc',
                fontSize: '16px',
                textAlign: 'center',
                marginBottom: '20px'
              }}
              disabled={isSubmitting}
            />
            
            {/* Exibe mensagem de erro, se houver */}
            {codeError && (
              <div className={classes.errorMessage} style={{
                color: '#d32f2f',
                textAlign: 'center',
                marginBottom: '15px',
                fontSize: '14px'
              }}>
                {codeError}
              </div>
            )}
            
            <RoundedButton
              className={classes.acceptButton}
              text={isSubmitting ? "PROCESSANDO..." : "RESGATAR"}
              type="submit"
              disabled={!inviteCode.trim() || isSubmitting}
            />
          </form>
          
          <p style={{ marginTop: '20px', textAlign: 'center', color: '#666' }}>
            Vincule seu perfil a uma outra conta
          </p>
        </>
      )}

      {/* Botão de retorno ao painel - presente em ambos os casos */}
      {hasCurrentLink && (
        <RoundedButton
          className={classes.returnButton}
          text="VOLTAR AO PAINEL"
          onClick={redirectToDashboard}
        />
      )}
    </StateView>
  );
};

export default NoInviteView;